/**
 * Fills the "About the Designer" block with the three newest flats of that
 * designer's collection.
 *
 * The markup is fetched rather than rendered with the page because Liquid
 * cannot sort a whole collection: it sees only the first 50 products, in the
 * collection's own order, which is not "newest" for every designer. Asking for
 * the collection URL with sort_by lets Shopify do the sorting, and the Section
 * Rendering API returns just the three flats rather than a whole page.
 *
 * Renders nothing and says nothing if the request fails: this block is a nice
 * extra beside the designer copy, never the reason someone opened the page.
 */
class DesignerNewestFlats extends HTMLElement {
  #fetched = false;

  connectedCallback() {
    // The PDP renders this twice, once for the mobile accordion and once for
    // the desktop column, so both fill themselves independently.
    this.#load();
  }

  async #load() {
    if (this.#fetched) return;
    this.#fetched = true;

    const url = this.dataset.url;
    if (!url) return;

    try {
      const response = await fetch(url);
      if (!response.ok) return;

      const markup = (await response.text()).trim();
      if (!markup) return;

      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const list = parsed.querySelector('.wf-designer-flats');
      if (!list || !list.children.length) {
        this.#hideIfAlone();
        return;
      }

      this.replaceChildren(list);
      this.removeAttribute('hidden');
    } catch {
      // Left empty and hidden, as it began.
      this.#hideIfAlone();
    }
  }

  /**
   * The block was shown because flats were expected. If none came back and
   * there is no designer copy, image or link beside them, hide the whole block
   * — heading included — rather than leave "About the Designer" over nothing.
   */
  #hideIfAlone() {
    const block = this.closest('[data-designer-block]');
    if (!block) return;
    const other = block.querySelector(
      '.wf-product-details__designer-description, .wf-product-details__designer-image',
    );
    // style, not [hidden]: the column's own display rule would outrank it.
    if (!other) block.style.display = 'none';
  }
}

if (!customElements.get('designer-newest-flats')) {
  customElements.define('designer-newest-flats', DesignerNewestFlats);
}
